import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Loader2, Building2 } from 'lucide-react';
import { vendorAPI } from '../../services/api';

const VendorSearchInput = ({
  value = '',
  vendorId = '',
  onSelect,
  onChange,
  onClear,
  placeholder = 'Search vendor by name, GSTIN, contact...',
  disabled = false,
  className = '',
  testId = 'vendor-search',
}) => {
  const [search, setSearch] = useState(value);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selected, setSelected] = useState(!!vendorId);
  const containerRef = useRef();
  const debounceRef = useRef();

  useEffect(() => {
    setSearch(value);
    setSelected(!!vendorId);
  }, [value, vendorId]);

  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const doSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setResults([]);
      setShowDropdown(false);
      return;
    }
    setLoading(true);
    try {
      const data = await vendorAPI.getAll({ search: query, page: 1, page_size: 15 });
      const items = Array.isArray(data) ? data : (data?.items || data || []);
      setResults(items);
      setShowDropdown(items.length > 0);
    } catch {
      setResults([]);
    }
    setLoading(false);
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    setSelected(false);
    onChange?.(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const handleSelect = (vendor) => {
    setSearch(vendor.name);
    setSelected(true);
    setShowDropdown(false);
    setResults([]);
    onSelect?.(vendor);
  };

  const handleClear = () => {
    setSearch('');
    setSelected(false);
    setResults([]);
    setShowDropdown(false);
    onClear?.();
  };

  const handleFocus = () => {
    if (results.length > 0 && !selected) setShowDropdown(true);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
        <input
          value={search}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={e => e.key === 'Escape' && setShowDropdown(false)}
          placeholder={placeholder}
          disabled={disabled}
          data-testid={testId}
          className={`w-full pl-7 pr-8 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200 ${
            selected ? 'bg-blue-50/50 border-blue-200' : ''
          } ${disabled ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''} ${className}`}
        />
        {loading && <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 animate-spin text-gray-400" />}
        {!loading && search && !disabled && (
          <button onClick={handleClear} className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-100" tabIndex={-1}>
            <X className="w-3 h-3 text-gray-400" />
          </button>
        )}
      </div>

      {showDropdown && results.length > 0 && (
        <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-[220px] overflow-y-auto">
          {results.map((vendor) => (
            <button
              key={vendor.id}
              onClick={() => handleSelect(vendor)}
              data-testid={`vendor-option-${vendor.id}`}
              className="w-full flex items-start gap-2.5 px-3 py-2 text-left hover:bg-blue-50 transition-colors border-b border-gray-50 last:border-0"
            >
              <div className="w-6 h-6 rounded bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Building2 className="w-3 h-3 text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-900 truncate">{vendor.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[9px] px-1 py-0.5 bg-gray-100 rounded text-gray-500 capitalize">{vendor.type}</span>
                  {vendor.gstin && <span className="text-[9px] text-gray-400 font-mono">{vendor.gstin}</span>}
                  {vendor.location && <span className="text-[9px] text-gray-400">{vendor.location}</span>}
                </div>
              </div>
              {vendor.currency && vendor.currency !== 'INR' && (
                <span className="text-[9px] font-semibold text-blue-600 flex-shrink-0">{vendor.currency}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {showDropdown && results.length === 0 && search.length >= 2 && !loading && (
        <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-center">
          <p className="text-[10px] text-gray-500">No vendors found for "{search}"</p>
        </div>
      )}
    </div>
  );
};

export default VendorSearchInput;
